// packages/ui/src/pages/ActivityLog.tsx
// Activity log page with filter bar, event list, WS subscription, and detail panel

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useActivity } from '../api/hooks'
import type { AuditEvent } from '../api/client'
import { EventRow } from '../components/activity/EventRow'
import { FilterBar, type ActivityFilters } from '../components/activity/FilterBar'

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getSeverity(event: AuditEvent): string {
  if (event.eventType.includes('error') || event.eventType.includes('denied')) return 'error'
  if (event.eventType.includes('warn')) return 'warn'
  if (event.eventType.includes('critical')) return 'critical'
  return 'info'
}

const DEFAULT_FILTERS: ActivityFilters = {
  channel: 'all',
  tool: 'all',
  host: 'all',
  severity: 'all',
}

export function ActivityLog() {
  const { data, isLoading, error } = useActivity({ limit: 100 })
  const [filters, setFilters] = useState<ActivityFilters>(DEFAULT_FILTERS)
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)
  const [wsEvents, setWsEvents] = useState<AuditEvent[]>([])
  const [autoScroll, setAutoScroll] = useState(true)
  const listRef = useRef<HTMLDivElement>(null)

  // Combine fetched entries with real-time WS events
  const allEntries = useMemo(() => {
    const fetched = data?.entries ?? []
    // Merge WS events (newer) on top, dedup by id
    const seen = new Set(fetched.map((e) => e.id))
    const unique = wsEvents.filter((e) => !seen.has(e.id))
    return [...unique, ...fetched]
  }, [data?.entries, wsEvents])

  // Extract unique values for filter dropdowns
  const channels = useMemo(() => {
    const set = new Set<string>()
    for (const e of allEntries) {
      if (e.sessionId) set.add(e.sessionId)
    }
    return Array.from(set).sort()
  }, [allEntries])

  const tools = useMemo(() => {
    const set = new Set<string>()
    for (const e of allEntries) {
      if (e.tool) set.add(e.tool)
    }
    return Array.from(set).sort()
  }, [allEntries])

  const hosts = useMemo(() => {
    const set = new Set<string>()
    for (const e of allEntries) {
      const host = e.params?.host as string | undefined
      if (host) set.add(host)
    }
    return Array.from(set).sort()
  }, [allEntries])

  // Apply filters
  const filteredEntries = useMemo(() => {
    return allEntries.filter((event) => {
      if (filters.severity !== 'all' && getSeverity(event) !== filters.severity) return false
      if (filters.tool !== 'all' && event.tool !== filters.tool) return false
      if (filters.host !== 'all') {
        const host = event.params?.host as string | undefined
        if (host !== filters.host) return false
      }
      if (filters.channel !== 'all' && event.sessionId !== filters.channel) return false
      return true
    })
  }, [allEntries, filters])

  // WebSocket subscription for real-time events
  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/api/dashboard/ws`
    let ws: WebSocket | null = null

    try {
      ws = new WebSocket(wsUrl)

      ws.onmessage = (msg) => {
        try {
          const parsed = JSON.parse(msg.data)
          if (parsed.type === 'agent:activity' && parsed.payload) {
            setWsEvents((prev) => [parsed.payload, ...prev])
          }
        } catch {
          // Ignore parse errors
        }
      }

      ws.onerror = () => {
        // Silently handle WS errors — data still loads via REST
      }
    } catch {
      // WebSocket not available — fallback to REST polling only
    }

    return () => {
      ws?.close()
    }
  }, [])

  // Auto-scroll to top on new events
  useEffect(() => {
    if (autoScroll && listRef.current) {
      listRef.current.scrollTop = 0
    }
  }, [filteredEntries.length, autoScroll])

  const handleEventClick = useCallback((event: AuditEvent) => {
    setSelectedEvent((prev) => (prev?.id === event.id ? null : event))
  }, [])

  if (error) {
    return (
      <div data-testid="activity-page" className="p-6">
        <h1 className="text-2xl font-bold mb-4">Activity</h1>
        <div data-testid="activity-error" className="text-red-400">
          Failed to load activity: {(error as Error).message}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="activity-page" className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <h1 className="text-2xl font-bold">Activity</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-400">
            <input
              type="checkbox"
              data-testid="auto-scroll-toggle"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded border-gray-600"
            />
            Auto-scroll
          </label>
          <span className="text-xs text-gray-500" data-testid="event-count">
            {filteredEntries.length} events
          </span>
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        channels={channels}
        tools={tools}
        hosts={hosts}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Event list */}
        <div
          ref={listRef}
          data-testid="event-list"
          className="flex-1 overflow-y-auto"
        >
          {isLoading ? (
            <div data-testid="activity-loading" className="p-6 text-center text-gray-500">
              Loading activity...
            </div>
          ) : filteredEntries.length === 0 ? (
            <div data-testid="activity-empty" className="p-6 text-center text-gray-500">
              No events match the current filters.
            </div>
          ) : (
            filteredEntries.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                onClick={handleEventClick}
                isSelected={selectedEvent?.id === event.id}
              />
            ))
          )}
        </div>

        {/* Detail panel */}
        {selectedEvent && (
          <div
            data-testid="event-detail-panel"
            className="w-80 border-l border-gray-800 overflow-y-auto bg-gray-900/50 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-200">Event Detail</h3>
              <button
                data-testid="close-detail"
                className="text-gray-500 hover:text-gray-300 text-sm"
                onClick={() => setSelectedEvent(null)}
              >
                x
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <span className="text-xs text-gray-500">Event Type</span>
                <p className="text-sm text-gray-200" data-testid="detail-event-type">
                  {selectedEvent.eventType}
                </p>
              </div>

              <div>
                <span className="text-xs text-gray-500">Timestamp</span>
                <p className="text-sm text-gray-200" data-testid="detail-timestamp">
                  {formatTimestamp(selectedEvent.createdAt)}
                </p>
              </div>

              {selectedEvent.tool && (
                <div>
                  <span className="text-xs text-gray-500">Tool</span>
                  <p className="text-sm text-gray-200" data-testid="detail-tool">
                    {selectedEvent.tool}
                  </p>
                </div>
              )}

              {selectedEvent.sessionId && (
                <div>
                  <span className="text-xs text-gray-500">Session</span>
                  <p className="text-sm text-gray-200 font-mono text-xs" data-testid="detail-session">
                    {selectedEvent.sessionId}
                  </p>
                </div>
              )}

              {selectedEvent.params && Object.keys(selectedEvent.params).length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">Parameters</span>
                  <pre
                    data-testid="detail-params"
                    className="text-xs text-gray-300 bg-gray-800 rounded p-2 mt-1 overflow-x-auto font-mono whitespace-pre-wrap"
                  >
                    {JSON.stringify(selectedEvent.params, null, 2)}
                  </pre>
                </div>
              )}

              {selectedEvent.result != null && (
                <div>
                  <span className="text-xs text-gray-500">Result</span>
                  <pre
                    data-testid="detail-result"
                    className="text-xs text-gray-300 bg-gray-800 rounded p-2 mt-1 overflow-x-auto font-mono whitespace-pre-wrap"
                  >
                    {typeof selectedEvent.result === 'string'
                      ? selectedEvent.result
                      : JSON.stringify(selectedEvent.result, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
